import { MutableRefObject, useRef, useEffect, useState } from "react"
import Button from "../../../../../components/Button/Button";
import Input from "../../../../../components/Input/input";
import { saveAddress, saveMultiStopAddresses, updateAddressCourier, updateOrderInfoOnAddressUpdate } from "../../../actions/order.actions";
import EditAddressMap from "./editAddressMap";
import EditAddressPaci from "./editAddressPaci";
import AddressTypes from "./addressTypeButton";
import AddNewAddressButton from "./addNewAddressButton";
import SaveAddressesDropDown from "./savedAddressesDropdown";
import RefineAddress from "./refineAddress";
import DistanceDifference from "./addressDisctanceDifference";
import { getInitialLatLngFromStreet } from "../../../actions/config.actions";
import { calculateLinearDistance } from "../../../../../utils/distance";

export default function EditAddress(props: any) {
  const formSubmitButtonRef = useRef() as MutableRefObject<{ click: Function }>;

  const [activeTab, setActiveTab] = useState('map');
  const [activeMode, setActiveMode] = useState('edit');
  const [updatedAddress, setUpdatedAddress]: [any, Function] = useState();
  const [selectedArea, setSelectedArea]: [any, Function] = useState();
  const [selectedBlock, setSelectedBlock]: [any, Function] = useState();
  const [selectedStreet, setSelectedStreet]: [any, Function] = useState();
  const [paciDetails, setPaciDetails]: [any, Function] = useState();
  const [selectedMapAddress, setSelectedMapAddress]: [any, Function] = useState();
  const [location, setLocation]: [any, Function] = useState();
  const [savedAddressId, setSavedAddressId]: [any, Function] = useState(null);
  const [distance, setDistance]: [any, Function] = useState(0);
  const [addressType, setAddressType]: [any, Function] = useState('HOME');
  
  // Multi-stop support
  const [updatedStops, setUpdatedStops] = useState<any[]>([]);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const isMultiStop = props?.isMultiStopOrder && props?.type === 'delivery' && props?.deliveryStops?.length > 0;

  // Initialize stops when deliveryStops prop changes
  useEffect(() => {
    if (isMultiStop && props.deliveryStops?.length > 0) {
      setUpdatedStops(props.deliveryStops.map((stop: any) => ({
        ...stop,
        type: stop.type || 'HOME'
      })));
    }
  }, [props.deliveryStops?.length, isMultiStop]);

  // Update current stop display when currentStopIndex changes
  useEffect(() => {
    if (isMultiStop) {
      const currentStop = updatedStops.length > currentStopIndex 
        ? updatedStops[currentStopIndex] 
        : props.deliveryStops?.[currentStopIndex];
      
      if (currentStop) {
        setLocation(currentStop?.location);
        setUpdatedAddress({ ...currentStop, type: currentStop?.type || addressType });
        setAddressType(currentStop?.type || 'HOME');
      }
    } else {
      setLocation(props?.address?.location);

      if(!props?.address?.type) {
        setUpdatedAddress({ ...props?.address, type: addressType });
      } else { 
        setUpdatedAddress({ ...props?.address });
        setAddressType(props?.address?.type);
      }
    }

    async function fetchMyAPI() {
      const addr = isMultiStop 
        ? (updatedStops.length > currentStopIndex ? updatedStops[currentStopIndex] : props?.deliveryStops?.[currentStopIndex]) 
        : props?.address;
      if(addr?.street_id) {
        const latLng : any = await getInitialLatLngFromStreet(addr?.area_id, addr?.block_id, addr?.street_id);
        const d = await calculateLinearDistance(latLng?.lat , latLng?.lng, addr?.location?._lat, addr?.location?._long);
        setDistance(d);
      }
    }
    fetchMyAPI();
  }, [props?.address?.id, currentStopIndex, isMultiStop, updatedStops]);

  function handleChange(e: any, name: string) {
    let address = {...updatedAddress};
    address[name] = e.target.value;
    setUpdatedAddress(address);
    
    // Update the stops array if multi-stop
    if (isMultiStop) {
      const newStops = [...updatedStops];
      newStops[currentStopIndex] = {...address};
      setUpdatedStops(newStops);
    }
  }
  
  const handleLocationChange = (newLocation: any) => {
    setLocation(newLocation);

    const addressWithLocation = {
      ...updatedAddress,
      location: newLocation,
      name: updatedAddress?.name 
    };
    setUpdatedAddress(addressWithLocation);
    
    if (isMultiStop) {
      const newStops = [...updatedStops];
      const currentStop = newStops[currentStopIndex] || {};
      newStops[currentStopIndex] = {
        ...currentStop,
        ...addressWithLocation,
        location: newLocation,
        name: updatedAddress?.name || currentStop?.name 
      };
      setUpdatedStops(newStops);
    }
  }
  
  const switchStop = (index: number) => {
    // Save current stop data before switching
    if (isMultiStop) {
      const newStops = [...updatedStops];
      newStops[currentStopIndex] = {
        ...updatedAddress, 
        location: location,
        name: updatedAddress?.name
      };
      setUpdatedStops(newStops);
    }
    
    setCurrentStopIndex(index);
  }

  const saveChanges = async () => {
    if (isMultiStop) {
      // Save current stop data before saving all
      const newStops = [...updatedStops];
      const currentStopInArray = newStops[currentStopIndex] || {};
      newStops[currentStopIndex] = {
        ...currentStopInArray,
        ...updatedAddress,
        location: location || updatedAddress?.location || currentStopInArray?.location,
        name: updatedAddress?.name || currentStopInArray?.name 
      };
      
      // Detect which stops were changed by comparing with original
      const changedStopIndices: number[] = [];
      
      newStops.forEach((stop: any, index: number) => {
        const originalStop = props.deliveryStops[index];
        if (originalStop) {
          // Check if any field changed
          const fieldsToCheck = ['name', 'contactName', 'contactPhone', 'area', 'block', 'street', 'avenue', 'building', 'floorNumber', 'roomNumber', 'type'];
          const hasFieldChanged = fieldsToCheck.some(field => stop[field] !== originalStop[field]);
          
          // Check if location changed
          const originalLat = originalStop.location?._lat || originalStop.location?.latitude;
          const originalLong = originalStop.location?._long || originalStop.location?.longitude;
          const newLat = stop.location?._lat || stop.location?.latitude;
          const newLong = stop.location?._long || stop.location?.longitude;
          const hasLocationChanged = originalLat !== newLat || originalLong !== newLong;
          
          if (hasFieldChanged || hasLocationChanged) {
            changedStopIndices.push(index);
          }
        }
      });
      
      // Process all stops to create addressStr
      const processedStops = newStops.map((stop: any) => {
        let str = '';
        let additionalText = (stop.type == 'HOME' || stop.type == 'STORE') ? 'Landmark' : 'RM';

        str = stop.type ? str.concat( ` | Type: ${stop.type}`) : str;
        str = stop.building ? str.concat( ` | BLDG: ${stop.building}`) : str;
        str = stop.floorNumber ? str.concat( ` | FLR: ${stop.floorNumber}`) : str;
        str = stop.roomNumber ? str.concat( ` | ${additionalText}: ${stop.roomNumber}`) : str;
        str = stop.area ? str.concat( ` | Area: ${stop.area}`) : str;
        str = stop.block ? str.concat( ` | Block: ${stop.block}`) : str;
        str = stop.street ? str.concat( ` | Street: ${stop.street}`) : str;
        str = stop.avenue ? str.concat( ` | ${stop.avenue}`) : str;

        return {
          ...stop,
          addressStr: str,
          isValid: false,
          location: stop.location,
          name: stop.name 
        };
      });

      // Check for duplicate names
      const nameCounts: { [key: string]: number[] } = {};
      processedStops.forEach((stop: any, index: number) => {
        const stopName = stop.name || stop.contactName || '';
        if (stopName) {
          if (!nameCounts[stopName]) {
            nameCounts[stopName] = [];
          }
          nameCounts[stopName].push(index + 1);
        }
      });

      // Find duplicates
      const duplicates: string[] = [];
      Object.keys(nameCounts).forEach(name => {
        if (nameCounts[name].length > 1) {
          duplicates.push(`${name} (Stops: ${nameCounts[name].join(', ')})`);
        }
      });

      if (duplicates.length > 0) {
        const errorMessage = `Duplicate stop names found: ${duplicates.join('; ')}`;
        throw new Error(errorMessage);
      }

      const orderValidation = props?.orderType !== 'COURIER' ? {
        ...props?.orderValidation,
        deliveryValidate: {
          ...props?.orderValidation.deliveryValidate,
          updated: true
        }
      } : {};

      // Save all stops with information about which ones changed
      await saveMultiStopAddresses(props?.orderId, props?.orderNo, processedStops, props?.fleetId, props?.orderType, orderValidation, changedStopIndices);
      
      // Close dialog after successful save
      if (props?.onClose) {
        props.onClose();
      }
      
    } else {
      // Single address save
      let str = '';
      let additionalText = (updatedAddress.type == 'HOME' || updatedAddress.type == 'STORE') ? 'Landmark' : 'RM';

      str = updatedAddress.type ? str.concat( ` | Type: ${updatedAddress.type}`) : str;
      str = updatedAddress.building ? str.concat( ` | BLDG: ${updatedAddress.building}`) : str;
      str = updatedAddress.floorNumber ? str.concat( ` | FLR: ${updatedAddress.floorNumber}`) : str;
      str = updatedAddress.roomNumber ? str.concat( ` | ${additionalText}: ${updatedAddress.roomNumber}`) : str;
      str = updatedAddress.area ? str.concat( ` | Area: ${updatedAddress.area}`) : str;
      str = updatedAddress.block ? str.concat( ` | Block: ${updatedAddress.block}`) : str;
      str = updatedAddress.street ? str.concat( ` | Street: ${updatedAddress.street}`) : str;
      str = updatedAddress.avenue ? str.concat( ` | ${updatedAddress.avenue}`) : str;

      const address = {
        ...updatedAddress,
        addressStr: str, isValid: false, type: addressType
      }
      
      
      setUpdatedAddress(address);

      const orderValidation = props?.orderType !== 'COURIER' ? props?.type === 'pickup' ? {
        ...props?.orderValidation,
        pickupValidate: {
          ...props?.orderValidation.pickupValidate,
          updated: true
        }
      } : {
        ...props?.orderValidation,
        deliveryValidate: {
          ...props?.orderValidation.deliveryValidate,
          updated: true
        }
      } : {};
      
      await Promise.all([
        ...(props?.orderType !== 'COURIER' ? [updateOrderInfoOnAddressUpdate(address, props?.type, props?.orderId, props?.orderNo)] : []),
        ...(props?.deliveryNo ? [updateAddressCourier(address, activeTab, props?.type, props?.deliveryNo, props?.orderType, props?.orderId)] : []),
        saveAddress(props?.orderId, props?.orderNo, props?.type, address, props?.fleetId, props?.orderType, props?.userId, orderValidation, savedAddressId, activeMode)
      ]);
    }
  }

  const changeActiveMode = (mode: string) => {
    setActiveMode(mode);
    setUpdatedAddress();
    setSavedAddressId(null);
    setDistance(0)
  }

  return (
    <>
      <div className="flex flex-col gap-4 p-2">
        {/* Multi-stop selector */}
        {isMultiStop && (
          <div className="border border-gray-300 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-gray-700">Select Stop to Edit:</div>
              <div className="text-xs text-gray-600">
                {currentStopIndex + 1} of {updatedStops.length}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {updatedStops.map((stop: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => switchStop(index)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all relative group ${
                    currentStopIndex === index
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">Stop {index + 1}</span>
                    {stop.name && <span className="text-xs opacity-80">• {stop.name}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {props.orderType !== 'COURIER' ? <>
          <AddNewAddressButton changeActiveMode={changeActiveMode} setUpdatedAddress={setUpdatedAddress} setSavedAddressId={setSavedAddressId} 
          setAddressType={setAddressType} setLocation={handleLocationChange}/>
          <SaveAddressesDropDown setUpdatedAddress={setUpdatedAddress} setSavedAddressId={setSavedAddressId} otherAddress={props.otherAddress} setAddressType={setAddressType}/>
        </> : <></>}
        <div>
          <EditAddressMap updatedAddress={updatedAddress} setUpdatedAddress={setUpdatedAddress} setPaciDetails={setPaciDetails}
            mode={activeTab} setSelectedMapAddress={setSelectedMapAddress} location={location} setLocation={handleLocationChange} />
          <RefineAddress addressStr={updatedAddress?.addressStr} name={updatedAddress?.name} area={updatedAddress?.area} block={updatedAddress?.block} street={updatedAddress?.street}/>
        </div>
        <DistanceDifference distance={distance} />
        <form onSubmit={(e: any) => {
          e.preventDefault();
          formSubmitButtonRef?.current?.click();
        }} className="flow-root">
          <div className="flex gap-2 flex-1 flex-col bg-white rounded-lg h-1/2 w-full">
            <fieldset className="mb-2 border border-solid border-gray-300 px-3 pb-3 rounded-lg">
              <legend className="text-sm px-1">Kuwait location details</legend>
              <EditAddressPaci updatedAddress={updatedAddress} setUpdatedAddress={setUpdatedAddress} location={location} setLocation={handleLocationChange}
                selectedArea={selectedArea} selectedBlock={selectedBlock} selectedStreet={selectedStreet} primaryStatus={props.primaryStatus} paciDetails={paciDetails}
                setSelectedArea={setSelectedArea} setSelectedBlock={setSelectedBlock} setSelectedStreet={setSelectedStreet} setDistance={setDistance}/>
            </fieldset>

            <fieldset className="mb-2 border border-solid border-gray-300 px-3 pb-3 rounded-lg">
              <legend className="text-sm px-1">Address details</legend>
              <AddressTypes updatedAddress={updatedAddress} setUpdatedAddress={setUpdatedAddress} addressType={addressType} setAddressType={setAddressType} onChange={handleChange}
              primaryStatus={props.primaryStatus} orderType={props.orderType}/>
            </fieldset>
            <fieldset className="mb-2 border border-solid border-gray-300 px-3 pb-3 rounded-lg">
              <legend className="text-sm px-1">Contact details</legend>
              <div className="flex gap-2 justify-between items-center text-base font-medium text-gray-900">
                <div className="flex-1">
                  <Input
                    rules={{
                      required: false,
                      minLength: 3,
                      maxLength: 25,
                      disabled: props.primaryStatus === "OTHERS" ? true : false,
                    }}
                    labelText="Contact name" onChange={(e: any) => handleChange(e, "contactName")} value={updatedAddress?.contactName ?? ''} defaultValue={updatedAddress?.contactName ?? ''} type="text" placeholder="contactName" />
                </div>
                <div className="flex-1">
                  <Input
                    rules={{
                      required: false,
                      minLength: 7,
                      maxLength: 11,
                      pattern: "[0-9]{7,11}",
                      disabled: props.primaryStatus === "OTHERS" ? true : false,
                    }}
                    labelText="contact phone" onChange={(e: any) => handleChange(e, "contactPhone")} value={updatedAddress?.contactPhone ?? ''} defaultValue={updatedAddress?.contactPhone ?? ''} type="tel" placeholder="contactPhone" />
                </div>
              </div>
            </fieldset>
            {props?.primaryStatus === "OTHERS" ? <></> : <div className="flex items-center justify-end">
              <Button
                ref={formSubmitButtonRef}
                type="submit"
                notify={false}
                id="editAddress"
                cb={saveChanges}
                text="Save"
                loadingText="Saving..."
                successMsg="Changes saved successfully"
              />
            </div>}
          </div>
        </form>
      </div>
    </>
  )
}
